// BC accounts dashboard (F3): summary bar, every Business-Center ad account
// as a table row (status chip, balance, spend windows, ads-running dot,
// owning-client badges or "Free"), and the cross-account recent-ads panel.
// Click-through to /dashboard/accounts/:id for the detail + v1 actions.
//
// Data comes from the admin backend's bc_* Postgres cache (refresh-on-read):
// GET /admin/dashboard/accounts and GET /admin/dashboard/recent-ads. When the
// last TikTok sync failed the backend still serves the cache with
// `stale: true` plus an admin-only, already-redacted sync error — surfaced
// here as a banner, never an empty page.
//
// SECURITY: money values are NUMERIC-fidelity STRINGS from the backend and
// are rendered verbatim — never parsed into floats. Everything shown is
// TikTok RESPONSE data sanitized server-side; no secrets ever reach this UI.
//
// CHROME: NEXOVA AdminShell + nexus primitives (StatCard, Card sections,
// DataTable, tone status chips). The shared sub-components and data hooks are
// unchanged from the legacy screen; AccountDetailPage still imports them here.

import { useEffect, useState } from "react";
import { Link } from "react-router-dom";

import { api } from "@/api/client";
import {
  financeSlotsLabel,
  financeView,
  financeViewTitle,
  lacksFinanceView,
  NO_FINANCE_VIEW_TEXT,
  type FinanceViewFields,
} from "@/api/financeview";
import { errMessage } from "@/lib/errMessage";
import { cn } from "@/lib/utils";
import { AdminShell } from "@/components/nexus/AdminShell";
import {
  DataTable,
  StatCard,
  ToneBadge,
  type Column,
  type Tone,
} from "@/components/nexus";
import { Skeleton } from "@/components/ui/skeleton";
import { Alert, AlertDescription, AlertTitle } from "@/components/ui/alert";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import { Pager } from "./nexus/controls";

// The BC dashboard returns EVERY account for the summary rollup; the table is
// paged CLIENT-SIDE so a large Business Center doesn't render 90+ rows at once.
const PAGE_SIZE = 50;

export interface ClientRef {
  id: string;
  name: string;
}

interface DashboardSummary {
  total: number;
  active: number; // display_status APPROVED
  suspended: number; // display_status PUNISHED
  unlinked: number; // zero allowlist links ("free")
}

// One dashboard row. It extends FinanceViewFields because the backend now
// derives, per account, WHETHER any authorized identity can read its
// wallet/budget balance (finance_readable + finance_slots, ADR-0054). Those two
// are optional in the TYPE only to tolerate an older deploy that omits them —
// see @/api/financeview.
interface DashboardAccount extends FinanceViewFields {
  account_id: string;
  name: string;
  display_status: string;
  status: string;
  account_type: string;
  currency: string;
  // Money fields are decimal STRINGS ("" = unknown); rendered verbatim.
  balance: string;
  spend_7d: string;
  spend_30d: string;
  ads_running: boolean;
  clients: ClientRef[]; // [] = free
  synced_at: number; // unix seconds; 0 = never
}

interface DashboardAccountsResponse {
  summary: DashboardSummary;
  stale: boolean;
  synced_at: number;
  // Admin-only, already-redacted bc_sync failure message (absent on success).
  sync_error?: string;
  // Admin-only: Business Centers that refused a FINANCE read (TikTok 40002 "no
  // finance permission"). Empty/omitted when none. Their accounts still sync —
  // only the wallet/budget balance is blank, which used to look like an
  // unexplained gap.
  finance_denied_bcs?: string[];
  accounts: DashboardAccount[];
}

interface RecentAdRow {
  account_id: string;
  account_name: string;
  ad_id: string;
  ad_name: string;
  campaign_name: string;
  spend: string;
  currency: string;
  synced_at: number;
}

interface RecentAdsResponse {
  ads: RecentAdRow[];
}

// Map TikTok's 4-value display status (research doc §8) onto a NEXOVA tone chip.
// Unknown values render verbatim in a neutral chip rather than disappearing.
const statusChips: Record<string, { label: string; tone: Tone }> = {
  SHOW_ACCOUNT_STATUS_APPROVED: { label: "approved", tone: "credit" },
  SHOW_ACCOUNT_STATUS_PUNISHED: { label: "punished", tone: "danger" },
  SHOW_ACCOUNT_STATUS_IN_REVIEW: { label: "in review", tone: "warn" },
  SHOW_ACCOUNT_STATUS_NOT_APPROVED: { label: "not approved", tone: "na" },
};

export function AccountStatusChip({ displayStatus }: { displayStatus: string }) {
  const chip = statusChips[displayStatus] ?? {
    label: displayStatus || "unknown",
    tone: "na" as Tone,
  };
  return <ToneBadge tone={chip.tone}>{chip.label}</ToneBadge>;
}

// Collapse a redacted bc_sync error whose tail is a wall of advertiser IDs
// ("No permission to operate advertiser: <id>,<id>,…") into a short count plus
// the id list (which the banner tucks behind a details toggle so it can never
// inflate the layout). Any other message has no id-wall and is returned
// verbatim (ids === undefined).
//
// The capture stops at ';' on purpose: since ADR-0054 the backend wraps the
// upstream message in its own aggregate — "advertiser info failed for N of M
// advertisers: <upstream>; ids: <capped sample> (+K more)" — and a greedy
// `.+$` would swallow that tail and count "ids:", "(+180" and "more)" as
// advertisers. The tokens are additionally filtered to plausible advertiser
// ids (TikTok's are long digit strings), so stray words or the 500-rune
// truncation ellipsis can never inflate the count either.
export function parseSyncError(syncError: string): {
  summary: string;
  ids?: string;
} {
  const m = syncError.match(/no permission to operate advertisers?:\s*([^;]+)/i);
  if (!m) return { summary: syncError };
  const ids = m[1]
    .trim()
    .split(/[\s,]+/)
    .filter((t) => /^\d{6,}$/.test(t));
  // Nothing id-shaped after the marker: there is no wall to collapse.
  if (ids.length === 0) return { summary: syncError };
  const prefix = syncError.slice(0, m.index).trim();
  const summary = `${prefix ? `${prefix} ` : ""}No permission to operate ${ids.length} advertiser${
    ids.length === 1 ? "" : "s"
  }`;
  return { summary, ids: ids.join(",") };
}

// The refresh-on-read contract: upstream trouble serves the cache flagged
// stale (with the admin-only redacted sync error) instead of failing the page.
// The advertiser-id permission wall is COLLAPSED (see parseSyncError) so it
// can't push the page grid past the viewport; min-w-0 + overflow-x-auto keep the
// banner itself contained defensively.
export function StaleBanner({ syncError }: { syncError?: string }) {
  const parsed = syncError ? parseSyncError(syncError) : null;
  return (
    <Alert variant="destructive" className="min-w-0 overflow-x-auto">
      <AlertTitle>Showing cached data</AlertTitle>
      <AlertDescription>
        <div className="min-w-0">
          <p>
            The last TikTok sync did not complete
            {parsed ? <>: {parsed.summary}</> : "."} Values may be out of date.
          </p>
          {parsed?.ids && (
            <details className="mt-1">
              <summary className="cursor-pointer text-xs underline-offset-2 hover:underline">
                show IDs
              </summary>
              <div className="mt-1 font-mono text-xs break-all [overflow-wrap:anywhere]">
                {parsed.ids}
              </div>
            </details>
          )}
        </div>
      </AlertDescription>
    </Alert>
  );
}

// The finance-permission banner: the sync SUCCEEDED, but one or more Business
// Centers refused the finance read, so their accounts show no balance. This is
// a diagnosis, not a failure — hence a warn-toned (non-destructive) banner,
// visually distinct from the stale/sync-error one. The fix is per BC: grant the
// authorized TikTok identity the finance role in that Business Center.
export function FinanceDeniedBanner({ bcIds }: { bcIds: string[] }) {
  return (
    <Alert className="min-w-0 border-nx-warn/30 bg-nx-warn-soft">
      <AlertTitle className="text-nx-warn">
        Finance role missing in Business Center
        {bcIds.length === 1 ? "" : "s"}:
      </AlertTitle>
      <AlertDescription>
        <p className="text-nx-text-2">
          <span className="font-mono break-all [overflow-wrap:anywhere]">
            {bcIds.join(", ")}
          </span>{" "}
          — accounts owned by them show no wallet/budget balance.
        </p>
      </AlertDescription>
    </Alert>
  );
}

// Green dot = the cached activity probe found delivering campaigns.
export function AdsRunningDot({ running }: { running: boolean }) {
  return (
    <span
      aria-label={running ? "ads running" : "no ads running"}
      className={cn(
        "inline-block size-2.5 rounded-full",
        running ? "bg-nx-live" : "bg-nx-na",
      )}
    />
  );
}

// Owning-client badges, or the "Free" badge for an unlinked account.
export function ClientBadges({ clients }: { clients: ClientRef[] }) {
  if (clients.length === 0) {
    return <ToneBadge tone="na">Free</ToneBadge>;
  }
  return (
    <div className="flex flex-wrap gap-1">
      {clients.map((c) => (
        <ToneBadge key={c.id} tone="brand">
          {c.name}
        </ToneBadge>
      ))}
    </div>
  );
}

export function syncedLabel(unixSeconds: number): string {
  if (unixSeconds <= 0) return "never synced";
  return `synced ${new Date(unixSeconds * 1000).toLocaleString()}`;
}

// A balance is "unavailable" ONLY when the backend returns the empty string —
// a genuine "0" / "0.00" is a real, known balance and must never be flagged.
export function balanceUnavailable(balance: string): boolean {
  return balance === "";
}

// The compact per-account FINANCIAL-VIEW indicator, shared by the dashboard
// table, the BC detail page, and the Accounts-hub drawer.
//
//   finance_readable true  -> a credit-toned "fin view" badge naming the slots.
//   finance_readable false -> a warn-toned "no fin view" badge: no authorized
//                             identity can read this account's wallet/budget
//                             balance, so its owning BC needs a Finance role.
//   fields ABSENT          -> NOTHING. An older backend does not report finance
//                             visibility, and rendering "no fin view" for every
//                             account during a deploy-skew window would invent
//                             a permission problem that does not exist.
//
// This COMPOSES with the balance-unavailable badge rather than replacing it: an
// account can carry a real prepaid figure and still have no financial view (the
// figure came from a different, non-finance source), and an account can be both
// balance-unavailable and finance-blind — which is the diagnosis for the blank.
export function FinanceViewBadge({ account }: { account: FinanceViewFields }) {
  const view = financeView(account);
  if (!view.known) return null;
  return (
    <span title={financeViewTitle(view)} className="w-fit">
      <ToneBadge tone={view.readable ? "credit" : "warn"}>
        {view.readable ? "fin view" : "no fin view"}
      </ToneBadge>
    </span>
  );
}

// The one-line spelling of the same verdict, for the roomier detail surfaces
// (BC detail page, account drawer) where a badge alone is too terse.
export function financeViewLine(account: FinanceViewFields): string {
  const view = financeView(account);
  return view.readable
    ? `via ${financeSlotsLabel(view.slots)}`
    : NO_FINANCE_VIEW_TEXT;
}

// Why an operator sees no balance. If the whole sync is stale/failed the cache
// simply couldn't refresh; otherwise the master token most likely lacks the
// Finance-Analyst grant on this account (the client-facing "finance access
// pending" case). Kept admin-blunt so ops can act on it.
export function balanceIssueReason(stale: boolean, syncError?: string): string {
  return stale || syncError ? "sync stale/failed" : "finance access pending";
}

export function DashboardPage() {
  const [data, setData] = useState<DashboardAccountsResponse | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const [recentAds, setRecentAds] = useState<RecentAdRow[]>([]);
  const [recentLoading, setRecentLoading] = useState(true);
  const [recentError, setRecentError] = useState<string | null>(null);

  // Ops observability: filter the table down to accounts whose balance the
  // backend could not resolve ("" balance).
  const [onlyBalanceIssues, setOnlyBalanceIssues] = useState(false);
  // …and to accounts NO authorized identity can read a balance for
  // (finance_readable false). Composes with the balance filter (AND).
  const [onlyNoFinanceView, setOnlyNoFinanceView] = useState(false);
  // Client-side pager offset over the (filtered) account list.
  const [offset, setOffset] = useState(0);

  async function loadAccounts() {
    setLoading(true);
    setError(null);
    try {
      const res = await api.get<DashboardAccountsResponse>(
        "/admin/dashboard/accounts",
      );
      setData(res);
    } catch (err) {
      setError(errMessage(err));
    } finally {
      setLoading(false);
    }
  }

  async function loadRecentAds() {
    setRecentLoading(true);
    setRecentError(null);
    try {
      const res = await api.get<RecentAdsResponse>(
        "/admin/dashboard/recent-ads",
      );
      setRecentAds(res.ads);
    } catch (err) {
      setRecentError(errMessage(err));
    } finally {
      setRecentLoading(false);
    }
  }

  useEffect(() => {
    void loadAccounts();
    void loadRecentAds();
  }, []);

  const accountColumns: Column<DashboardAccount>[] = [
    {
      key: "name",
      header: "Name",
      cell: (a) => (
        <div className="grid">
          <Link
            to={`/dashboard/accounts/${a.account_id}`}
            className="font-medium text-nx-text underline-offset-4 hover:underline"
          >
            {a.name}
          </Link>
          <span className="font-mono text-xs text-nx-text-3">
            {a.account_id}
          </span>
        </div>
      ),
    },
    {
      key: "status",
      header: "Status",
      cell: (a) => <AccountStatusChip displayStatus={a.display_status} />,
    },
    {
      key: "balance",
      header: "Balance",
      // The figure (or the balance-unavailable badge) PLUS the per-account
      // financial-view indicator. The two compose deliberately: a shown figure
      // with "no fin view" tells the operator the number is not a live
      // finance read, and a blank balance with "no fin view" is the diagnosis.
      cell: (a) => (
        <div className="grid gap-0.5">
          {balanceUnavailable(a.balance) ? (
            <>
              <ToneBadge tone="warn" className="w-fit">
                balance unavailable
              </ToneBadge>
              <span className="text-xs text-nx-text-3">
                {balanceIssueReason(data?.stale ?? false, data?.sync_error)}
              </span>
            </>
          ) : (
            <span className="font-mono text-sm text-nx-text tabular-nums">
              {a.balance} {a.currency}
            </span>
          )}
          <FinanceViewBadge account={a} />
        </div>
      ),
    },
    {
      key: "spend7",
      header: "Spend 7d",
      cell: (a) => (
        <span className="font-mono text-sm text-nx-text-2 tabular-nums">
          {a.spend_7d}
        </span>
      ),
    },
    {
      key: "spend30",
      header: "Spend 30d",
      cell: (a) => (
        <span className="font-mono text-sm text-nx-text-2 tabular-nums">
          {a.spend_30d}
        </span>
      ),
    },
    {
      key: "ads",
      header: "Ads",
      cell: (a) => <AdsRunningDot running={a.ads_running} />,
    },
    {
      key: "clients",
      header: "Clients",
      cell: (a) => <ClientBadges clients={a.clients} />,
    },
  ];

  const recentColumns: Column<RecentAdRow>[] = [
    {
      key: "ad",
      header: "Ad",
      cell: (ad) => (
        <div className="grid">
          <span className="font-medium text-nx-text">{ad.ad_name}</span>
          <span className="font-mono text-xs text-nx-text-3">{ad.ad_id}</span>
        </div>
      ),
    },
    {
      key: "campaign",
      header: "Campaign",
      cell: (ad) => <span className="text-nx-text-2">{ad.campaign_name}</span>,
    },
    {
      key: "account",
      header: "Account",
      cell: (ad) => (
        <Link
          to={`/dashboard/accounts/${ad.account_id}`}
          className="text-nx-text-2 underline-offset-4 hover:underline"
        >
          {ad.account_name}
        </Link>
      ),
    },
    {
      key: "spend",
      header: "Spend",
      cell: (ad) => (
        <span className="font-mono text-sm text-nx-text-2 tabular-nums">
          {ad.spend}
        </span>
      ),
    },
    {
      key: "currency",
      header: "Currency",
      cell: (ad) => <span className="text-nx-text-2">{ad.currency}</span>,
    },
  ];

  const allAccounts = data?.accounts ?? [];
  const balanceIssueCount = allAccounts.filter((a) =>
    balanceUnavailable(a.balance),
  ).length;
  // Only offer the financial-view filter when THIS backend actually reports the
  // fields — otherwise the toggle would be a control that always filters to
  // zero (or, worse, implies every account is finance-blind).
  const financeAware = allAccounts.some((a) => financeView(a).known);
  const noFinanceViewCount = allAccounts.filter(lacksFinanceView).length;
  // Both toggles compose (AND).
  const visibleAccounts = allAccounts.filter(
    (a) =>
      (!onlyBalanceIssues || balanceUnavailable(a.balance)) &&
      (!onlyNoFinanceView || lacksFinanceView(a)),
  );
  const pageAccounts = visibleAccounts.slice(offset, offset + PAGE_SIZE);
  const emptyMessage =
    onlyBalanceIssues && onlyNoFinanceView
      ? "No accounts with both a balance issue and no financial view."
      : onlyNoFinanceView
        ? "No accounts without a financial view."
        : onlyBalanceIssues
          ? "No accounts with balance issues."
          : "No accounts cached yet.";

  return (
    <AdminShell>
      <div className="grid gap-6">
        <div className="flex items-center justify-between">
          <div>
            <h2 className="font-heading text-lg font-semibold">Dashboard</h2>
            <p className="mt-1 text-sm text-nx-text-2">
              Every ad account in the Business Center.
            </p>
          </div>
          {data && (
            <span className="text-sm text-nx-text-3">
              {syncedLabel(data.synced_at)}
            </span>
          )}
        </div>

        {/* Surface a failed sync PROMINENTLY (admin-side counterpart of the
            client message) — whenever the cache is stale OR the backend
            returned a redacted sync error, never buried. */}
        {(data?.stale || data?.sync_error) && (
          <StaleBanner syncError={data?.sync_error} />
        )}

        {/* Distinct from the stale banner: the cache is fine, the finance GRANT
            is missing in those BCs. Silent 40002 made visible. */}
        {(data?.finance_denied_bcs?.length ?? 0) > 0 && (
          <FinanceDeniedBanner bcIds={data?.finance_denied_bcs ?? []} />
        )}

        {error && (
          <Alert variant="destructive">
            <AlertTitle>Could not load dashboard</AlertTitle>
            <AlertDescription>{error}</AlertDescription>
          </Alert>
        )}

        {loading ? (
          <div className="grid gap-2">
            <Skeleton className="h-20 w-full" />
            <Skeleton className="h-8 w-full" />
            <Skeleton className="h-8 w-full" />
          </div>
        ) : data ? (
          <>
            {/* Summary bar: the backend-computed rollup (spec §5). */}
            <div className="grid grid-cols-2 gap-4 md:grid-cols-4">
              <StatCard label="Total accounts" value={data.summary.total} />
              <StatCard label="Active" value={data.summary.active} tone="credit" />
              <StatCard
                label="Suspended"
                value={data.summary.suspended}
                tone="debit"
              />
              <StatCard
                label="Unlinked (free)"
                value={data.summary.unlinked}
                tone="muted"
              />
            </div>

            {/* Accounts table */}
            <Card>
              <CardHeader>
                <div className="flex flex-wrap items-start justify-between gap-2">
                  <div>
                    <CardTitle>Accounts</CardTitle>
                    <CardDescription>
                      Every ad account in the Business Center. Click a name for
                      detail and actions.
                      {visibleAccounts.length > 0 &&
                        ` Showing ${offset + 1}–${offset + pageAccounts.length} of ${visibleAccounts.length}.`}
                    </CardDescription>
                  </div>
                  <div className="flex flex-wrap items-center gap-4">
                    <label className="flex items-center gap-2 text-xs text-nx-text-2">
                      <input
                        type="checkbox"
                        checked={onlyBalanceIssues}
                        onChange={(e) => {
                          setOnlyBalanceIssues(e.target.checked);
                          // The filtered set changes size — restart at page 1.
                          setOffset(0);
                        }}
                        className="accent-nx-brand"
                      />
                      Only balance issues
                      {balanceIssueCount > 0 ? ` (${balanceIssueCount})` : ""}
                    </label>
                    {financeAware && (
                      <label className="flex items-center gap-2 text-xs text-nx-text-2">
                        <input
                          type="checkbox"
                          checked={onlyNoFinanceView}
                          onChange={(e) => {
                            setOnlyNoFinanceView(e.target.checked);
                            setOffset(0);
                          }}
                          className="accent-nx-brand"
                        />
                        Only without financial view
                        {noFinanceViewCount > 0
                          ? ` (${noFinanceViewCount})`
                          : ""}
                      </label>
                    )}
                  </div>
                </div>
              </CardHeader>
              <CardContent>
                <DataTable
                  columns={accountColumns}
                  rows={pageAccounts}
                  rowKey={(a) => a.account_id}
                  empty={emptyMessage}
                />
                <Pager
                  offset={offset}
                  pageSize={PAGE_SIZE}
                  count={pageAccounts.length}
                  onOffset={setOffset}
                />
              </CardContent>
            </Card>
          </>
        ) : null}

        {/* Recent-ads panel: top spenders across ALL accounts. */}
        <Card>
          <CardHeader>
            <CardTitle>Recent ads</CardTitle>
            <CardDescription>
              Top ads by spend across all accounts
              {recentAds.length > 0 && (
                <> · {syncedLabel(recentAds[0].synced_at)}</>
              )}
              .
            </CardDescription>
          </CardHeader>
          <CardContent>
            {recentError && (
              <Alert variant="destructive">
                <AlertTitle>Could not load recent ads</AlertTitle>
                <AlertDescription>{recentError}</AlertDescription>
              </Alert>
            )}
            {recentLoading ? (
              <div className="grid gap-2">
                <Skeleton className="h-8 w-full" />
                <Skeleton className="h-8 w-full" />
              </div>
            ) : (
              <DataTable
                columns={recentColumns}
                rows={recentAds}
                rowKey={(ad) => `${ad.account_id}-${ad.ad_id}`}
                empty="No recent ads cached yet."
              />
            )}
          </CardContent>
        </Card>
      </div>
    </AdminShell>
  );
}
